import {
  Card,
  Col,
  Row,
  Typography,
  Table,
  Breadcrumb,
  Button,
  Input,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import React from "react";
import DateRange from "../../Common/DateRange";
import { ReportsRouter } from "../ReportsRouter";
import CallCenterSalesLogService from "../../../services/CallCenterSalesLogService";
import * as XLSX from "xlsx";

const { Title } = Typography;

export const SalesLogReport: React.FC<any> = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<any>([]);
  const [data, setData] = useState<any>([]);
  const [filteredData, setFilteredData] = useState<any>([]);
  const [searchText, setSearchText] = useState<string>("");
  const location = useLocation();

  const getRoleNameAndTitles = () => {
    if (location.pathname.includes("/ctm-reports/")) {
      return {
        roleName: "CALL_TEAM",
        columnTitle: "Call Team Member",
        breadcrumbTitle: "Call Team Reports",
        cardTitle: "Call Team Member",
      };
    } else if (location.pathname.includes("/stm-reports/")) {
      return {
        roleName: "SUCCESS_TEAM",
        columnTitle: "Success Team Member",
        breadcrumbTitle: "Success Team Reports",
        cardTitle: "Success Team Member",
      };
    } else if (location.pathname.includes("/acm-reports/")) {
      return {
        roleName: "ACCOUNT_MANAGER",
        columnTitle: "Account Manager",
        breadcrumbTitle: "Account Manager Reports",
        cardTitle: "Account Manager",
      };
    } else if (location.pathname.includes("/hi5-acm-reports/")) {
      return {
        roleName: "HI5_ACM_MEMBER",
        columnTitle: "Hi5 Account Manager",
        breadcrumbTitle: "Hi5 Account Manager Reports",
        cardTitle: "Hi5 Account Manager",
      };
    }
    return {
      roleName: "",
      columnTitle: "Unknown Role",
      breadcrumbTitle: "Unknown Reports",
      cardTitle: "Unknown Member",
    };
  };

  useEffect(() => {
    const { roleName } = getRoleNameAndTitles();
    if (
      dateRange[0] !== undefined &&
      dateRange[1] !== undefined &&
      roleName !== ""
    ) {
      getReport(
        `${dateRange[0]}T00:00:00`,
        `${dateRange[1]}T23:59:59`,
        roleName
      );
    }
    // eslint-disable-next-line
  }, [dateRange, location.pathname]);

  const getReport = (startDate: string, endDate: string, roleName: string) => {
    setLoading(true);
    CallCenterSalesLogService.getSalesLogReport(startDate, endDate, roleName)
      .then((response) => {
        const filteredResponseData = response.data.filter(
          (item: any) =>
            item.user.username !== "az.success.team.crm@oneamz.com" &&
            item.user.username !== "stm@oneamz.com" &&
            item.user.username !== "duygu.karakilic.hi5acm.crm@oneamz.com"
        );
        setData(filteredResponseData);
        setFilteredData(filteredResponseData);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getUniqueSalesDataKeys = (data: any[]) => {
    const allKeys = data.reduce((keys, item) => {
      return keys.concat(Object.keys(item.salesDataMap));
    }, []);
    return Array.from(new Set(allKeys));
  };

  const salesDataKeys = getUniqueSalesDataKeys(data);

  const { columnTitle, breadcrumbTitle, cardTitle } = getRoleNameAndTitles();

  const extractNumericValue = (value: string): number => {
    const match = value.match(/^(\d+)/);
    return match ? parseFloat(match[1]) : 0;
  };

  const capitalize = (text: string) => {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const columns: ColumnsType<any> = [
    {
      title: columnTitle,
      dataIndex: "user",
      key: "user",
      fixed: "left",
      width: 200,
      ellipsis: true,
      sorter: (a, b) => a?.user?.firstName.localeCompare(b?.user?.firstName),
      defaultSortOrder: "ascend",
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`${record?.user?.firstName} ${record?.user?.lastName}`}
          >
            <b>
              {record?.user?.firstName} {record?.user?.lastName}
            </b>
          </div>
        );
      },
    },
    ...(location.pathname.includes("/ctm-reports/sales-log-report")
      ? salesDataKeys.filter((key: any) => {
          return data.some((item: any) => {
            const value = item.salesDataMap[key] || "0 (0)";
            const match = value.match(/^(\d+)\s\((\d+)\)$/);
            const currentValue = match ? parseFloat(match[1]) : 0;
            const previousValue = match ? parseFloat(match[2]) : 0;
            return currentValue !== 0 || previousValue !== 0;
          });
        })
      : salesDataKeys
    ).map((key: any) => ({
      title: `${capitalize(key)}*`,
      dataIndex: "salesDataMap",
      key,
      width: 150,
      align: "center" as const,
      sorter: (a: any, b: any) => {
        const aValue = extractNumericValue(a.salesDataMap[key] || "0");
        const bValue = extractNumericValue(b.salesDataMap[key] || "0");
        return aValue - bValue;
      },
      render: (salesDataMap: any) => {
        return `${salesDataMap[key] || "0 (0)"} qty`;
      },
    })),
  ];

  const handleSearch = (e: any) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    if (value === "") {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item: any) => {
        const fullName =
          `${item?.user?.firstName} ${item?.user?.lastName}`.toLowerCase();
        return fullName.includes(value);
      });
      setFilteredData(filtered);
    }
  };

  const downloadExcel = () => {
    // Kolon başlıklarını belirle
    const headers = columns
      .filter(
        (col: any) =>
          col.dataIndex !== "user" && col.dataIndex !== "salesDataMap"
      )
      .map((col) => col.title as string);

    // İlk kolon başlığı için `user` adını ve soyadını ekle
    headers.unshift("Member");

    // Kolon verilerini ayarlamak
    const formattedData = filteredData.map((item: any) => {
      // user ve salesDataMap kolonlarını kaldır
      const { user, salesDataMap, ...rest } = item;

      // Sales data map'in anahtarlarını içeren bir nesne oluştur
      const salesData: any = salesDataKeys.reduce((acc: any, key: any) => {
        acc[key] = `${salesDataMap[key] || "0 (0)"} qty`; // Varsayılan değer
        return acc;
      }, {} as { [key: string]: string });

      // `user` bilgilerini birleştir
      const userInfo = `${user?.firstName || ""} ${user?.lastName || ""}`;

      // Birleştirilmiş veri nesnesi
      return { Member: userInfo, ...rest, ...salesData };
    });

    // Summary için toplamları hesapla
    const salesDataTotals = salesDataKeys.map((key: any) => {
      // Hem ana hem de parantez içindeki değerlerin toplamlarını hesaplayalım
      const totals = filteredData.reduce(
        (total: any, record: any) => {
          const value = record.salesDataMap[key];
          const match = value?.match(/^(\d+)\s\((\d+)\)$/); // Ana değer ve parantez içindeki değeri yakalar
          if (match) {
            total.current += parseFloat(match[1]); // Ana değeri toplar
            total.previous += parseFloat(match[2]); // Parantez içindeki değeri toplar
          }
          return total;
        },
        { current: 0, previous: 0 } // Toplamları tutmak için başlangıç değerleri
      );
      return `${totals.current} (${totals.previous}) qty`; // Toplamları formatlı olarak döndürür
    });

    // Summary nesnesini oluştur
    const summaryRow: any = {
      Member: "Total",
      ...salesDataKeys.reduce<Record<string, string>>(
        (acc: any, key: any, index: any) => {
          acc[key] = salesDataTotals[index];
          return acc;
        },
        {}
      ),
    };

    // Summary satırını `formattedData` listesine ekle
    formattedData.push(summaryRow);

    // Tablo verilerini almak
    const ws = XLSX.utils.json_to_sheet(formattedData, {
      header: headers,
    });

    // Çalışma kitabı oluşturmak
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales Log Report");

    // Excel dosyasını indirmek
    XLSX.writeFile(wb, "Sales Log Report.xlsx");
  };

  return (
    <>
      <Row>
        <Col>
          <Title className="onemaz-page-title" level={3}>
            Reports
          </Title>
          <Breadcrumb
            items={[
              {
                title: <NavLink to="/">Home</NavLink>,
              },
              {
                title: breadcrumbTitle,
              },
              {
                title: "Sales Log Report",
              },
            ]}
          />
        </Col>
      </Row>
      <ReportsRouter />
      <Card
        className="card-customers"
        title={
          <>
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <div
                className="customer-card-title"
                style={{ color: "#EEC73B", fontSize: 22, fontWeight: 600 }}
              >
                {cardTitle}
              </div>
              <div className="customer-card-info">Sales Log Report</div>
            </div>
            <Input
              className="search-input"
              addonBefore={<SearchOutlined />}
              placeholder="Search By Name"
              value={searchText}
              onChange={handleSearch}
              style={{ marginBottom: 16 }}
              size="large"
            />
          </>
        }
        extra={
          <Row justify={"end"}>
            <DateRange thisMonth={true} onChangeDateRange={setDateRange} />
            <Button
              style={{ marginLeft: "20px" }}
              size={"large"}
              icon={<DownloadOutlined />}
              onClick={downloadExcel}
              type="primary"
            >
              Export
            </Button>
          </Row>
        }
        bordered={true}
        style={{ marginTop: "30px" }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24} style={{ margin: "0 0 5px 10px" }}>
            <span>
              {" "}
              * The values in parentheses represent the previous period based on
              the range selected from the date filter.
            </span>
          </Col>
        </Row>
        <Table
          loading={isLoading}
          scroll={{ x: "100%" }}
          sticky
          rowKey={(record) => record.user.id.toString()}
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          summary={(pageData) => {
            let salesDataTotals: (string | null)[] = [];
            if (location.pathname.includes("/ctm-reports/sales-log-report")) {
              salesDataTotals = salesDataKeys.map((key: any) => {
                const totals = pageData.reduce(
                  (total, record) => {
                    const value = record.salesDataMap[key];
                    const match = value?.match(/^(\d+)\s\((\d+)\)$/);
                    if (match) {
                      const currentValue = parseFloat(match[1]);
                      const previousValue = parseFloat(match[2]);
                      if (currentValue !== 0 || previousValue !== 0) {
                        total.current += currentValue;
                        total.previous += previousValue;
                      }
                    }
                    return total;
                  },
                  { current: 0, previous: 0 }
                );
                return totals.current !== 0 || totals.previous !== 0
                  ? `${totals.current} (${totals.previous}) qty`
                  : null;
              });
            } else {
              salesDataTotals = salesDataKeys.map((key: any) => {
                const totals = pageData.reduce(
                  (total, record) => {
                    const value = record.salesDataMap[key];
                    const match = value?.match(/^(\d+)\s\((\d+)\)$/);
                    if (match) {
                      total.current += parseFloat(match[1]);
                      total.previous += parseFloat(match[2]);
                    }
                    return total;
                  },
                  { current: 0, previous: 0 }
                );
                return `${totals.current} (${totals.previous}) qty`;
              });
            }

            return (
              <>
                <Table.Summary.Row className="summary-cell">
                  <Table.Summary.Cell
                    index={0}
                    colSpan={1}
                    className="summary-cell"
                  >
                    <b>Total</b>
                  </Table.Summary.Cell>
                  {location.pathname.includes("/ctm-reports/sales-log-report")
                    ? salesDataKeys.map((key: any, index: any) => {
                        const total = salesDataTotals[index];
                        if (total) {
                          return (
                            <Table.Summary.Cell
                              index={index + 1}
                              key={index}
                              align="center"
                              className="summary-cell"
                            >
                              <b>{total}</b>
                            </Table.Summary.Cell>
                          );
                        } else {
                          return null;
                        }
                      })
                    : salesDataKeys.map((key: any, index: any) => (
                        <Table.Summary.Cell
                          index={index + 1}
                          key={index}
                          align="center"
                          className="summary-cell"
                        >
                          <b>{salesDataTotals[index]}</b>
                        </Table.Summary.Cell>
                      ))}
                </Table.Summary.Row>
              </>
            );
          }}
        />
      </Card>
    </>
  );
};
